import { Layout, Tooltip } from 'antd';
import {
  CreditCardOutlined,
  FileTextOutlined,
  TeamOutlined,
  StopOutlined,
  LogoutOutlined,
  ExperimentOutlined,
  ShopOutlined,
  ReadOutlined,
  PercentageOutlined,
  CalendarOutlined,
  BankOutlined,
  SettingOutlined,
  NumberOutlined,
  GoldOutlined,
  TruckOutlined,
  TagsOutlined,
  BlockOutlined,
  BorderOuterOutlined,
  BookOutlined,
  AreaChartOutlined,
  ProfileOutlined,
  HistoryOutlined,
  AppstoreOutlined,
  AppstoreAddOutlined,
  WalletOutlined,
  DollarOutlined,
  // Stage 6B.7E — Profit & Loss + Balance Sheet icons.
  LineChartOutlined,
  AccountBookOutlined,
  // Stage 6B.9C — Contra Voucher icon (cash/bank ↔ cash/bank).
  SwapOutlined,
  // Stage 6B.9D — Fixed Assets Register icon.
  HddOutlined,
  // Stage 6B.9E — Investments Register icon.
  FundOutlined,
  // Inventory Dashboard landing icon.
  DashboardOutlined,
  // Stage 6E.1 — Bank Reconciliation + Cheque Tracking icons.
  ReconciliationOutlined,
  CheckSquareOutlined,
  // Stage SCAN-2 — tenant scanner fleet (My Scanners).
  WifiOutlined,
  // Stage SCAN-6 — Ingest Monitor.
  MonitorOutlined,
  // Stage 6E.2 — Year-End Closing icon.
  CalendarTwoTone,
  // Stage 6E.3 — Debtors / Creditors Aging icons.
  UserOutlined,
  ContainerOutlined,
  // Stage 6P.1 + 6P.2 — Purchase Request / Purchase Order icons.
  ShoppingCartOutlined,
  // Stage 6RFQ — Request for Quotation icon.
  SolutionOutlined,
  // Stage HRMS-1 — HRMS foundation icons.
  ApartmentOutlined,
  IdcardOutlined,
  // Stage HRMS-3 — organization chart icon.
  PartitionOutlined,
  // Stage WO-1A — Production / Work Orders icon.
  DeploymentUnitOutlined,
  ScissorOutlined,
  // Stage WO-1B — Scan Station icon.
  ScanOutlined
} from '@ant-design/icons';
import { NavLink } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
// Local brand logo. Vite handles the PNG import at build time.
import glassILogo from '../images/Glass-i_Logo.png';

const { Sider } = Layout;

// Authenticated app sidebar — modern-SaaS rail (Linear / Stripe /
// Vercel feel). Uses custom NavLink rows rather than AntD Menu so
// the active state, hover, spacing, and logout treatment can be
// tuned tightly. AntD primitives are used elsewhere; the sidebar is
// the one place where the chrome benefits from a hand-built rail.
//
// Layout (top → bottom):
//   1. Brand block (h-16, brand-blue gradient — matches topbar)
//   2. Section: ERP MODULES (label + Stage-6A placeholder, scrollable)
//   3. Section: BILLING (Subscription / Invoices / Users)
//   4. Pinned: Log out (rose hover, separated by border)
//   5. Pinned: Operational status footer

function SectionLabel({ children }) {
  // Small brand-blue dot prefixes the label as a visual rhythm anchor,
  // echoing the active-row left rail without competing with it.
  return (
    <p className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] text-ink-500 font-semibold m-0 px-3 mt-5 mb-2">
      <span
        aria-hidden="true"
        className="inline-block h-1 w-1 rounded-full bg-brand-500/60"
      />
      <span>{children}</span>
    </p>
  );
}

// Single row builder — handles four shapes:
//   - navigable (NavLink with active state)
//   - action (button, e.g. Log out, with optional danger tint)
//   - disabled (StopOutlined / Users when suspended)
//   - collapsed (icon-only with right-side tooltip)
function NavRow({ to, icon, label, disabled, tooltip, action, danger, collapsed, end }) {
  // Slightly faster than the default tailwind transition so hovering
  // from row to row never feels "syrupy" — enterprise UIs reward
  // responsiveness. Easing is `ease-out` for a soft settle.
  const baseRow =
    'group relative flex items-center gap-2.5 h-9 rounded-lg text-[13px] transition-[background-color,color,box-shadow] duration-150 ease-out';
  const layout = collapsed ? 'justify-center px-0' : 'px-3';

  // Disabled (cursor-not-allowed, ink-300 throughout)
  if (disabled) {
    const node = (
      <div
        className={`${baseRow} ${layout} text-ink-300 cursor-not-allowed select-none`}
        aria-disabled="true"
      >
        <span className="text-[15px] leading-none text-ink-300 shrink-0">
          {icon}
        </span>
        {!collapsed ? <span className="truncate">{label}</span> : null}
      </div>
    );
    return (
      <Tooltip title={tooltip || label} placement="right">
        {node}
      </Tooltip>
    );
  }

  // Action button (Log out). Danger flag tints hover rose.
  if (typeof action === 'function') {
    const stateCls = danger
      ? 'text-ink-700 hover:bg-rose-50 hover:text-rose-600'
      : 'text-ink-700 hover:bg-surface-50 hover:text-ink-900';
    const iconCls = danger
      ? 'text-ink-500 group-hover:text-rose-500'
      : 'text-ink-500 group-hover:text-ink-700';
    const inner = (
      <button
        type="button"
        onClick={action}
        className={`${baseRow} ${layout} ${stateCls} w-full bg-transparent border-0 cursor-pointer text-left appearance-none focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300`}
      >
        <span
          className={`text-[15px] leading-none transition-colors shrink-0 ${iconCls}`}
        >
          {icon}
        </span>
        {!collapsed ? <span className="truncate">{label}</span> : null}
      </button>
    );
    return collapsed ? (
      <Tooltip title={label} placement="right">
        {inner}
      </Tooltip>
    ) : (
      inner
    );
  }

  // Navigable row — NavLink with isActive render-prop so the icon
  // can pick up the brand-tint when its row is active. The active
  // surface uses a thin brand-tinted ring (1px inset) to add
  // dimensionality without competing with the soft brand-50 fill;
  // hover states get a touch of bg-surface-100 for crisper contrast
  // against the off-white sidebar.
  const inner = (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `${baseRow} ${layout} ${
          isActive
            ? 'bg-brand-50 text-brand-700 font-semibold shadow-[inset_0_0_0_1px_rgba(24,119,242,0.10)]'
            : 'text-ink-700 hover:bg-surface-100/70 hover:text-ink-900'
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* Left accent rail — only visible when the row is active.
              Sits inside the rounded row so it inherits the brand-50
              background's left edge, giving a stronger "you are here"
              cue than the tint alone. */}
          {isActive ? (
            <span
              aria-hidden="true"
              className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-brand-600"
            />
          ) : null}
          <span
            className={`text-[15px] leading-none transition-colors duration-150 shrink-0 ${
              isActive
                ? 'text-brand-600'
                : 'text-ink-500 group-hover:text-brand-500'
            }`}
          >
            {icon}
          </span>
          {!collapsed ? <span className="truncate">{label}</span> : null}
        </>
      )}
    </NavLink>
  );
  return collapsed ? (
    <Tooltip title={label} placement="right">
      {inner}
    </Tooltip>
  ) : (
    inner
  );
}

export default function Sidebar({ collapsed = false, onBreakpoint }) {
  const { me, logout } = useAuth();

  const isMaster = me && me.isMaster === true;
  const effectiveStatus =
    me && me.subscription ? me.subscription.effectiveStatus : 'active';
  const isSuspended = effectiveStatus === 'suspended';

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={208}
      collapsedWidth={56}
      breakpoint="lg"
      onBreakpoint={onBreakpoint}
      style={{
        background: '#FCFCFD',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        height: '100vh',
        zIndex: 100
      }}
      className="border-r border-surface-200/80 transition-all duration-200"
    >
      <div className="h-full flex flex-col">
        {/* Brand block — solid Facebook blue, with a soft radial
            top-light that matches the topbar's atmospheric depth.
            The two form one continuous brand strip at the corner. */}
        <div
          className="flex items-center justify-center h-16 shrink-0 relative overflow-hidden"
          style={{
            backgroundColor: '#1877F2',
            backgroundImage:
              'radial-gradient(ellipse 200px 90px at 50% 0%, rgba(255,255,255,0.14), transparent 70%)',
            boxShadow:
              'inset 0 1px 0 rgba(255,255,255,0.12), 0 1px 0 rgba(15,23,42,0.06)'
          }}
          aria-label="Glass-i"
        >
          <img
            src={glassILogo}
            alt="Glass-i"
            className={`block w-auto object-contain ${
              collapsed ? 'h-10 max-w-[44px]' : 'h-12 max-w-[140px]'
            }`}
          />
        </div>

        {/* Scrollable nav area */}
        <div className="flex-1 min-h-0 overflow-y-auto pb-2">
          {/* --- ERP MASTERS --- */}
          {!collapsed ? <SectionLabel>ERP Masters</SectionLabel> : null}
          <nav
            className={`flex flex-col gap-px ${collapsed ? 'mt-2' : 'px-2'}`}
            aria-label="ERP masters navigation"
          >
            <NavRow
              to="/erp/masters/customers"
              icon={<ShopOutlined />}
              label="Customers"
              collapsed={collapsed}
            />
            {/* Stage 6B.3 — Supplier Master. Tenant-schema; same shell
                pattern as Customers, simpler popup (3 add steps / 5
                edit tabs). */}
            <NavRow
              to="/erp/masters/suppliers"
              icon={<TruckOutlined />}
              label="Suppliers"
              collapsed={collapsed}
            />
            {/* Stage 6B.8A-1 — UOM Master with GSTN UQC mapping. */}
            <NavRow
              to="/erp/masters/uoms"
              icon={<TagsOutlined />}
              label="UOM Master"
              collapsed={collapsed}
            />
            {/* Stage 6B.8A-2A — Item Configuration Masters (3 tabs). */}
            <NavRow
              to="/erp/masters/item-config"
              icon={<BlockOutlined />}
              label="Item Configuration"
              collapsed={collapsed}
            />
            {/* Stage 6B.8A-4 — Item Master grid (main daily-driver
                listing page). The "Create Item" CTA inside opens the
                Stage 6B.8A-3 Product Creation Studio. Both share the
                `erp.masters.items.*` permissions enforced server-side. */}
            <NavRow
              to="/erp/masters/items"
              icon={<AppstoreAddOutlined />}
              label="Item Master"
              collapsed={collapsed}
            />
            {/* Stage INTERLAYER-STOCK-1 — Interlayer (PVB/EVA/SGP) film items.
                Consumable films stocked in m², bought in rolls/kg. Shares the
                erp.masters.items.* permissions server-side. */}
            <NavRow
              to="/erp/masters/interlayers"
              icon={<BlockOutlined />}
              label="Interlayer Items"
              collapsed={collapsed}
            />
            {/* Stage DGU-CONSUMABLE-1 — DGU consumables (spacer / desiccant /
                sealants / corner keys). Consumable BOM items in m / kg / pcs.
                Shares the erp.masters.items.* permissions server-side. */}
            <NavRow
              to="/erp/masters/dgu-consumables"
              icon={<BorderOuterOutlined />}
              label="DGU Consumables"
              collapsed={collapsed}
            />
            {/* Stage 6B.8A-3I — Ready-Made Glass Concept Master.
                Reusable architectural design templates (panels + hardware
                BOQ) that drive future quotation auto-fill. Shares the
                erp.masters.items.* permissions server-side. */}
            <NavRow
              to="/erp/masters/glass-concepts"
              icon={<GoldOutlined />}
              label="Concept Studio"
              collapsed={collapsed}
            />
            {/* Stage 6B.8A-3K — Process / Fabrication Master.
                Spreadsheet-style maintenance of process rates + a
                thickness-wise rate matrix that feeds future Proforma /
                Invoice / Costing. Shares the erp.masters.items.* perms. */}
            <NavRow
              to="/erp/masters/processes"
              icon={<BlockOutlined />}
              label="Process Master"
              collapsed={collapsed}
            />
            {/* Stage 6B.8A-3K.1 — Thickness Master.
                Shared canonical list of glass thicknesses consumed by
                Process Master and (future) Glass Product / Concept /
                Proforma / Production / Inventory. */}
            <NavRow
              to="/erp/masters/thicknesses"
              icon={<BlockOutlined />}
              label="Thickness Master"
              collapsed={collapsed}
            />
            {/* Stage 6B.8A-3K.3 — Production Department Master.
                Main / Sub (Manual / Machine) hierarchy reused by Process
                Master and (future) Work Order, Production routing,
                Barcode scanning, Costing and Reports. Reuses
                erp.masters.items.* permissions server-side. */}
            <NavRow
              to="/erp/masters/production-departments"
              icon={<BlockOutlined />}
              label="Production Departments"
              collapsed={collapsed}
            />
            {/* Stage 6B.8A-3N — Glass Category Master.
                Drives the Item-create Category dropdown (was a frozen
                JS constant before). Inline-CRUD; reuses items.* perms. */}
            <NavRow
              to="/erp/masters/glass-categories"
              icon={<BlockOutlined />}
              label="Glass Category Master"
              collapsed={collapsed}
            />
            {/* Stage 6OC.1 — Other Charges Master.
                Surcharge / add-on rule master + preview engine. Master
                data only (no ledger / stock / Proforma yet). Uses its own
                erp.other_charges.* permissions server-side. */}
            <NavRow
              to="/erp/masters/other-charges"
              icon={<BlockOutlined />}
              label="Other Charges Master"
              collapsed={collapsed}
            />
            {/* Stage 6Q — Manual Charge Master. Catalogue of document-level
                ad-hoc "additional cost" charges picked on the Proforma. */}
            <NavRow
              to="/erp/masters/manual-charges"
              icon={<BlockOutlined />}
              label="Manual Charge Master"
              collapsed={collapsed}
            />
            {/* Stage 6PL-1 — Customer Price Lists. Per-customer rates for
                items / fabrication / processes / charges. READ is open to
                every role (a salesperson must be able to explain a quote);
                every WRITE is master-only server-side. */}
            <NavRow
              to="/erp/masters/price-lists"
              icon={<TagsOutlined />}
              label="Price Lists"
              collapsed={collapsed}
            />
            {/* Stage 6B.8A-3M — Glass Type + Fabrication Masters.
                Independent flat masters (NOT linked to Process Master).
                Inline-CRUD only; reuses erp.masters.items.* perms. */}
            <NavRow
              to="/erp/masters/glass-types"
              icon={<BlockOutlined />}
              label="Glass Type Master"
              collapsed={collapsed}
            />
            {/* Stage WO-2A — Glass Type → Production Route config. Each glass
                type's department route (type-driven routing). Reuses
                erp.masters.items.* perms server-side. */}
            <NavRow
              to="/erp/masters/glass-type-routing"
              icon={<PartitionOutlined />}
              label="Glass Type Routing"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/masters/fabrications"
              icon={<BlockOutlined />}
              label="Fabrication Master"
              collapsed={collapsed}
            />
            {/* Stage 6CM.2 — Proforma Settings nav removed (use Company Defaults). */}
            {/* Stage 6Q.4 — tenant-wide Proforma grid column visibility. */}
            <NavRow
              to="/erp/masters/proforma-grid-settings"
              icon={<SettingOutlined />}
              label="Proforma Grid Columns"
              collapsed={collapsed}
            />
            {/* Stage 6CM.1 — Company Defaults Master (central default setup values). */}
            <NavRow
              to="/erp/masters/company-defaults"
              icon={<SettingOutlined />}
              label="Company Defaults"
              collapsed={collapsed}
            />
          </nav>

          {/* Stage 6Q.1 — Sales Proforma Invoice Foundation. Draft-invoice
              precursor; master-bound editor with WASM calc grid. No
              accounting / stock / tax-invoice yet. RBAC server-side via
              `erp.sales.proforma.*`. */}
          {!collapsed ? <SectionLabel>Sales</SectionLabel> : null}
          <nav
            className={`flex flex-col gap-px ${collapsed ? 'mt-2' : 'px-2'}`}
            aria-label="Sales navigation"
          >
            <NavRow
              to="/erp/sales/proformas"
              icon={<FileTextOutlined />}
              label="Proforma"
              collapsed={collapsed}
            />
            {/* Stage SALES-PROFORMA-APPROVAL-1 — L1/L2/L3 approval inbox. RBAC
                server-side via `erp.sales.proforma.approval.view` + the
                approver keys. */}
            <NavRow
              to="/erp/sales/proforma-approvals"
              icon={<CheckSquareOutlined />}
              label="Proforma Approvals"
              collapsed={collapsed}
            />
            {/* Sales Tax Invoice (GST) — Phase 1B. Draft/import/edit/discard;
                posting arrives in Phase 2. RBAC server-side via
                `erp.sales.invoice.*`. */}
            <NavRow
              to="/erp/sales/invoices"
              icon={<ProfileOutlined />}
              label="Tax Invoices"
              collapsed={collapsed}
            />
          </nav>

          {/* Stage WO-1A — Production / Work Orders. Turns a CONFIRMED
              proforma into a piece-tracked, QR-barcoded work order
              (release → routes → pieces → barcodes). RBAC server-side
              via `erp.production.workorder.*`. */}
          {!collapsed ? <SectionLabel>Production</SectionLabel> : null}
          <nav
            className={`flex flex-col gap-px ${collapsed ? 'mt-2' : 'px-2'}`}
            aria-label="Production navigation"
          >
            <NavRow
              to="/erp/production/work-orders"
              icon={<DeploymentUnitOutlined />}
              label="Work Orders"
              collapsed={collapsed}
            />
            {/* Stage WO-1B — shop-floor Scan Station (advance pieces by barcode).
                RBAC server-side via erp.production.workorder.piece.scan. */}
            <NavRow
              to="/erp/production/scan"
              icon={<ScanOutlined />}
              label="Scan Station"
              collapsed={collapsed}
            />
            {/* Stage SCAN-2 — map each Glass-i-allocated ESP32 scanner to a
                department and pick entry vs exit. Read is open to every role
                (erp.production.scan_device.view); the page itself hides the
                edit controls from non-master users, and the PATCH route is
                requireMaster-gated server-side. */}
            <NavRow
              to="/erp/production/scan-devices"
              icon={<WifiOutlined />}
              label="My Scanners"
              collapsed={collapsed}
            />
            {/* Stage SCAN-6 — Ingest Monitor: every record the fleet sent and
                the engine's verdict on each. Read is open to every role
                (erp.production.scan_ingest.view); deleting a record is
                master-only and the POST route is requireMaster-gated
                server-side. */}
            <NavRow
              to="/erp/production/scan-ingest"
              icon={<MonitorOutlined />}
              label="Ingest Monitor"
              collapsed={collapsed}
            />
            {/* Internal Breakage → Rework Request — MFA-approved breakage
                approvals inbox. RBAC server-side via
                erp.production.workorder.breakage.approve. */}
            <NavRow
              to="/erp/production/breakage-approvals"
              icon={<CheckSquareOutlined />}
              label="Breakage Approvals"
              collapsed={collapsed}
            />
            {/* Stage PRODUCTION-CUTTING-PLANNER-1 — group open work-order pieces
                into OPT / cutting plans, track cut completion, export CNC XLS.
                RBAC server-side via erp.production.cutting_planner.*. */}
            <NavRow
              to="/erp/production/cutting-planner"
              icon={<ScissorOutlined />}
              label="Cutting Planner"
              collapsed={collapsed}
              end
            />
            {/* Cutting-planner utilization report — per plan × thickness:
                area to cut, sheets utilized, yield, off-cut area/pcs, % utilization. */}
            <NavRow
              to="/erp/production/cutting-planner/utilization"
              icon={<AreaChartOutlined />}
              label="Cutting Utilization"
              collapsed={collapsed}
            />
          </nav>

          {/* Stage DISPATCH-PLAN-1 — Dispatch planning by delivery commitment.
              Plan dispatches for a delivery window and see how much of each
              committed order is ready. RBAC server-side via erp.dispatch.plan.*. */}
          {!collapsed ? <SectionLabel>Dispatch</SectionLabel> : null}
          <nav className={`flex flex-col gap-px ${collapsed ? 'mt-2' : 'px-2'}`} aria-label="Dispatch navigation">
            <NavRow
              to="/erp/dispatch/plans"
              icon={<TruckOutlined />}
              label="Dispatch Plans"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/dispatch/delivery-window"
              icon={<CalendarOutlined />}
              label="Delivery Window"
              collapsed={collapsed}
            />
          </nav>

          {/* Stage HRMS-1 — HRMS foundation. Employees / Departments /
              Designations masters + Customer Salesperson Mapping. Master
              data only (no payroll / attendance / leave / salary). RBAC
              server-side via `hrms.*` permissions. */}
          {!collapsed ? <SectionLabel>HRMS</SectionLabel> : null}
          <nav
            className={`flex flex-col gap-px ${collapsed ? 'mt-2' : 'px-2'}`}
            aria-label="HRMS navigation"
          >
            <NavRow
              to="/erp/hrms"
              end
              icon={<AppstoreOutlined />}
              label="Overview"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/hrms/employees"
              icon={<TeamOutlined />}
              label="Employees"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/hrms/departments"
              icon={<ApartmentOutlined />}
              label="Departments"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/hrms/designations"
              icon={<IdcardOutlined />}
              label="Designations"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/hrms/branches"
              icon={<BankOutlined />}
              label="Branches"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/hrms/salesperson-mapping"
              icon={<SolutionOutlined />}
              label="Salesperson Mapping"
              collapsed={collapsed}
            />
            {/* Stage HRMS-3 — organization, transfers, change history. */}
            <NavRow
              to="/erp/hrms/organization-chart"
              icon={<PartitionOutlined />}
              label="Organization Chart"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/hrms/employee-transfers"
              icon={<SwapOutlined />}
              label="Employee Transfers"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/hrms/employee-history"
              icon={<HistoryOutlined />}
              label="Employee History"
              collapsed={collapsed}
            />
          </nav>

          {/* Stage 6P.1 + 6P.2 — Purchase Request + Purchase Order
              Placement Foundation. Bridges supplier / item masters
              into the Accounts (voucher) flow. RBAC server-side via
              `erp.purchase.requests.*` and `erp.purchase.orders.*`. */}
          {!collapsed ? <SectionLabel>Purchase</SectionLabel> : null}
          <nav
            className={`flex flex-col gap-px ${collapsed ? 'mt-2' : 'px-2'}`}
            aria-label="Purchase navigation"
          >
            <NavRow
              to="/erp/purchase/requests"
              icon={<FileTextOutlined />}
              label="Purchase Requests"
              collapsed={collapsed}
            />
            {/* Stage 6RFQ — Request for Quotation. Sits between an approved
                PR and the PO. RBAC server-side via `erp.purchase.rfq.*`. */}
            <NavRow
              to="/erp/rfq"
              icon={<SolutionOutlined />}
              label="Requests for Quotation"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/purchase/orders"
              icon={<ShoppingCartOutlined />}
              label="Purchase Orders"
              collapsed={collapsed}
            />
            {/* Stage 6P.3 — GRN / Material Receipt Foundation. RBAC
                server-side via `erp.purchase.grn.*`. Reuses an icon
                already imported elsewhere in this file. */}
            <NavRow
              to="/erp/purchase/grns"
              icon={<ContainerOutlined />}
              label="Goods Receipt Notes"
              collapsed={collapsed}
            />
            {/* Stage 6P.4 — Purchase Bill Foundation + PO/GRN Matching.
                RBAC server-side via `erp.purchase.bills.*`. Reuses an
                icon already imported elsewhere in this file. */}
            <NavRow
              to="/erp/purchase/bills"
              icon={<FileTextOutlined />}
              label="Purchase Bills"
              collapsed={collapsed}
            />
            {/* Stage 6P.6 — Purchase Accounting Posting Foundation.
                Bridges Purchase Bills into the voucherPosting engine.
                Nested under the Purchase section so the four entries
                don't clutter the top level. Single permission family
                gate (`erp.purchase.accounting.*` + `erp.purchase.gst_itc.view`
                + `erp.purchase.supplier_payables.view`) on the routes.
                Reuses icons already imported in this file. */}
            {!collapsed ? (
              <p className="px-3 mt-3 mb-1 text-[10.5px] uppercase tracking-[0.14em] text-ink-400 font-semibold">
                Purchase Accounting
              </p>
            ) : null}
            <NavRow
              to="/erp/purchase-accounting/unposted-bills"
              icon={<FileTextOutlined />}
              label="Unposted Purchase Bills"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/purchase-accounting/postings"
              icon={<BookOutlined />}
              label="Purchase Accounting"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/purchase-accounting/gst-itc"
              icon={<PercentageOutlined />}
              label="GST ITC Register"
              collapsed={collapsed}
            />
            {/* Stage 6P.7 — Supplier Payment + Payment Voucher Integration.
                Three new rows immediately under Purchase Accounting so the
                AP workflow reads top-to-bottom: pay (Supplier Payments) →
                outstanding (Supplier Payables, now routed to the 6P.7
                analytics surface) → ledger (Payment Register). The
                previous 6P.6 read-only Supplier Payables row has been
                re-pointed to /erp/supplier-payments/payables — the new
                module is the canonical surface; the old
                /erp/purchase-accounting/supplier-payables route still
                exists for legacy bookmarks. RBAC server-side via
                `erp.supplier_payments.*` permissions. */}
            <NavRow
              to="/erp/supplier-payments"
              icon={<WalletOutlined />}
              label="Supplier Payments"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/supplier-payments/payables"
              icon={<WalletOutlined />}
              label="Supplier Payables"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/supplier-payments/register"
              icon={<BookOutlined />}
              label="Payment Register"
              collapsed={collapsed}
            />
            {/* Stage 6P.8 — Purchase Return / Debit Note Foundation.
                Four new rows appended immediately after the Supplier
                Payments group so AP workflows remain top-to-bottom:
                pay → outstanding → ledger → return / debit-note /
                register / ITC-reversals. Nested under the Stage 6A
                pl-2.5 / border-l / ml-2.5 template per the MEMORY.md
                Stage 5N-13.7 + 6A nesting convention. RBAC server-side
                via `erp.purchase.returns.*` permissions. */}
            {!collapsed ? (
              <p className="px-3 mt-3 mb-1 text-[10.5px] uppercase tracking-[0.14em] text-ink-400 font-semibold">
                Purchase Returns
              </p>
            ) : null}
            <div className={collapsed ? '' : 'pl-2.5 border-l border-surface-200 ml-2.5'}>
              <NavRow
                to="/erp/purchase-returns"
                icon={<FileTextOutlined />}
                label="Purchase Returns"
                collapsed={collapsed}
              />
              <NavRow
                to="/erp/purchase-returns?return_type=MATERIAL_RETURN"
                icon={<FileTextOutlined />}
                label="Debit Notes"
                collapsed={collapsed}
              />
              <NavRow
                to="/erp/purchase-returns/register"
                icon={<BookOutlined />}
                label="Purchase Return Register"
                collapsed={collapsed}
              />
              <NavRow
                to="/erp/purchase-returns/itc-reversals"
                icon={<PercentageOutlined />}
                label="ITC Reversals"
                collapsed={collapsed}
              />
            </div>
            {/* Stage 6P.9 — TDS / TCS Foundation. Five new rows appended
                immediately after the Purchase Returns group so AP
                statutory workflows remain co-located with the rest of
                the purchase chain: rules → supplier profiles →
                deductions → challans → reports. Nested under the Stage
                6A pl-2.5 / border-l / ml-2.5 template per the MEMORY.md
                Stage 5N-13.7 + 6A nesting convention. RBAC server-side
                via `erp.tds_tcs.*` permissions. Reuses icons already
                imported in this file. */}
            {!collapsed ? (
              <p className="px-3 mt-3 mb-1 text-[10.5px] uppercase tracking-[0.14em] text-ink-400 font-semibold">
                TDS / TCS
              </p>
            ) : null}
            <div className={collapsed ? '' : 'pl-2.5 border-l border-surface-200 ml-2.5'}>
              <NavRow
                to="/erp/tds-tcs/rules"
                icon={<PercentageOutlined />}
                label="TDS/TCS Rules"
                collapsed={collapsed}
              />
              <NavRow
                to="/erp/tds-tcs/supplier-profiles"
                icon={<ProfileOutlined />}
                label="Supplier Tax Profiles"
                collapsed={collapsed}
              />
              <NavRow
                to="/erp/tds-tcs/deductions"
                icon={<FileTextOutlined />}
                label="TDS/TCS Deductions"
                collapsed={collapsed}
              />
              <NavRow
                to="/erp/tds-tcs/challans"
                icon={<WalletOutlined />}
                label="TDS Challans"
                collapsed={collapsed}
              />
              <NavRow
                to="/erp/tds-tcs/reports/supplier-summary"
                icon={<AreaChartOutlined />}
                label="TDS Reports"
                collapsed={collapsed}
              />
            </div>
          </nav>

          {/* Stage 6P.5 — Inventory Inward Posting Foundation. Stock
              snapshot + immutable stock ledger + a dedicated Purchase
              Inward posting surface that consumes GRNs / Purchase
              Bills. RBAC server-side via `erp.inventory.*`. Reuses
              icons already imported in this file. */}
          {!collapsed ? <SectionLabel>Inventory</SectionLabel> : null}
          <nav
            className={`flex flex-col gap-px ${collapsed ? 'mt-2' : 'px-2'}`}
            aria-label="Inventory navigation"
          >
            <NavRow
              to="/erp/inventory"
              icon={<DashboardOutlined />}
              label="Dashboard"
              collapsed={collapsed}
              end
            />
            <NavRow
              to="/erp/inventory/stock"
              icon={<AppstoreOutlined />}
              label="Current Stock"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/inventory/stock-ledger"
              icon={<HistoryOutlined />}
              label="Stock Ledger"
              collapsed={collapsed}
            />
            <NavRow
              to="/erp/inventory/purchase-inward"
              icon={<ContainerOutlined />}
              label="Purchase Inward"
              collapsed={collapsed}
            />
            {/* Stage INVENTORY-OFFCUTS-1 — reusable leftover glass pieces
                (remnants) recorded after cutting. RBAC server-side via
                erp.inventory.offcuts.*. */}
            <NavRow
              to="/erp/inventory/offcuts"
              icon={<BlockOutlined />}
              label="Off-cuts"
              collapsed={collapsed}
            />
            {/* Stage INTERLAYER-STOCK-2 — separate PVB/interlayer inventory
                (m² balances + ledger), never mixed with glass stock. Reuses
                erp.inventory.stock.view server-side. */}
            <NavRow
              to="/erp/inventory/pvb"
              icon={<BlockOutlined />}
              label="PVB Inventory"
              collapsed={collapsed}
            />
            {/* Stage DGU-CONSUMABLE-2 — separate DGU-consumables inventory
                (spacer m / desiccant·sealant kg / corner-key pcs), never mixed
                with glass stock. Reuses erp.inventory.stock.view server-side. */}
            <NavRow
              to="/erp/inventory/dgu-consumables"
              icon={<BorderOuterOutlined />}
              label="DGU Inventory"
              collapsed={collapsed}
            />
            {/* Stage DGU-CONSUMABLE-3 — manual issue of DGU consumables to a
                work order (spacer / desiccant / sealants / corner keys),
                deducting from the separate DGU inventory. Reversible. */}
            <NavRow
              to="/erp/inventory/dgu-consumable-issues"
              icon={<BorderOuterOutlined />}
              label="DGU Issues"
              collapsed={collapsed}
            />
          </nav>

          {/* Stage 6B.1 — Accounts module foundation. Nested links sit
              under one section label, all gated server-side via the
              erp.accounts.* permissions. The four sub-pages share the
              same shell pattern as the Customer Master grid. */}
          {!collapsed ? <SectionLabel>Accounts</SectionLabel> : null}
          <nav
            className={`flex flex-col gap-px ${collapsed ? 'mt-2' : 'px-2'}`}
            aria-label="Accounts navigation"
          >
            <NavRow to="/erp/accounts"                    icon={<BankOutlined />}        label="Overview"          collapsed={collapsed} />
            <NavRow to="/erp/accounts/setup"              icon={<SettingOutlined />}     label="Setup"             collapsed={collapsed} />
            <NavRow to="/erp/accounts/ledgers"            icon={<ReadOutlined />}        label="Ledgers"           collapsed={collapsed} />
            <NavRow to="/erp/accounts/taxes"              icon={<PercentageOutlined />}  label="Taxes"             collapsed={collapsed} />
            <NavRow to="/erp/accounts/periods"            icon={<CalendarOutlined />}    label="Periods"           collapsed={collapsed} />
            <NavRow to="/erp/accounts/document-sequences" icon={<NumberOutlined />}      label="Doc Sequences"     collapsed={collapsed} />
            <NavRow to="/erp/accounts/opening-balances"   icon={<GoldOutlined />}        label="Opening Balances"  collapsed={collapsed} />
            {/* Stage 6B.4 — Journal Voucher list (first transactional
                accounting surface). Post / Cancel are L1-Approval-MFA
                gated server-side. */}
            <NavRow to="/erp/accounts/vouchers"           icon={<BookOutlined />}        label="Journal Vouchers"  collapsed={collapsed} />
            {/* Stage 6B.6 — payment + receipt vouchers in one grid. */}
            <NavRow to="/erp/accounts/payments"           icon={<WalletOutlined />}      label="Payment / Receipt" collapsed={collapsed} />
            {/* Stage SALES-RECEIPTS-1 — customer receipts & advances (posts via
                the Receipt Voucher engine; backs the Proforma Accounts review). */}
            <NavRow to="/erp/accounts/customer-receipts"  icon={<CreditCardOutlined />}  label="Customer Receipts" collapsed={collapsed} />
            {/* Stage 6B.7 — expense entry (paid + credit). */}
            <NavRow to="/erp/accounts/expenses"           icon={<DollarOutlined />}      label="Expenses"          collapsed={collapsed} />
            {/* Stage 6B.9C — Contra Voucher (cash↔bank, bank↔bank,
                cash↔cash). Reuses the voucherPosting engine. L1
                Approval-MFA on post / cancel / delete. */}
            <NavRow to="/erp/accounts/contras"            icon={<SwapOutlined />}        label="Contra Voucher"    collapsed={collapsed} />
            {/* Stage 6B.9D — Fixed Assets Register foundation.
                Depreciation + Disposal post through the engine.
                L1 Approval-MFA on delete / depreciate / dispose. */}
            <NavRow to="/erp/accounts/fixed-assets"       icon={<HddOutlined />}         label="Fixed Assets"      collapsed={collapsed} />
            {/* Stage 6B.9E — Investments Register foundation.
                Income / Dispose / Mature post through the engine.
                Valuation is register-only. L1 Approval-MFA on
                delete / record_income / dispose / mature. */}
            <NavRow to="/erp/accounts/investments"        icon={<FundOutlined />}        label="Investments"       collapsed={collapsed} />
            {/* Stage 6E.1 — Bank Reconciliation + Cheque Tracking
                (banking sub-section). Match bank statement lines to
                book entries; track cheque lifecycle. L1 Approval-MFA
                on reconciliation delete/lock/cancel and on cheque
                bounce/cancel/stop status changes. */}
            <NavRow to="/erp/accounts/bank-reconciliations" icon={<ReconciliationOutlined />} label="Bank Reconciliation" collapsed={collapsed} />
            <NavRow to="/erp/accounts/cheques"              icon={<CheckSquareOutlined />}    label="Cheque Tracking"     collapsed={collapsed} />
            {/* Stage 6E.2 — Year-End Closing + Opening Balance
                Voucher. L1 Approval-MFA on delete / post / opening-
                voucher / reopen. Preview is unprotected. */}
            <NavRow to="/erp/accounts/year-end-closing"     icon={<CalendarTwoTone twoToneColor="#1877F2" />} label="Year-End Closing"    collapsed={collapsed} />
            {/* Stage 6B.5 — read-only account reports. Nested
                under "Reports" so the four entries don't clutter the
                top level of the Accounts section. Single permission
                gate (`erp.accounts.reports.view`) on every route. */}
            {!collapsed ? (
              <p className="px-3 mt-3 mb-1 text-[10.5px] uppercase tracking-[0.14em] text-ink-400 font-semibold">
                Reports
              </p>
            ) : null}
            <NavRow to="/erp/accounts/reports/trial-balance"    icon={<AreaChartOutlined />}     label="Trial Balance"     collapsed={collapsed} />
            <NavRow to="/erp/accounts/reports/ledger-statement" icon={<ProfileOutlined />}       label="Ledger Statement"  collapsed={collapsed} />
            <NavRow to="/erp/accounts/reports/voucher-register" icon={<HistoryOutlined />}       label="Voucher Register"  collapsed={collapsed} />
            <NavRow to="/erp/accounts/reports/account-balances" icon={<AppstoreOutlined />}      label="Account Balances"  collapsed={collapsed} />
            {/* Stage 6B.7E — Profit & Loss + Balance Sheet. */}
            <NavRow to="/erp/accounts/reports/profit-loss"      icon={<LineChartOutlined />}     label="Profit & Loss"     collapsed={collapsed} />
            <NavRow to="/erp/accounts/reports/balance-sheet"    icon={<AccountBookOutlined />}   label="Balance Sheet"     collapsed={collapsed} />
            {/* Stage 6B.9G — Fixed Asset / Investment Schedules. */}
            <NavRow to="/erp/accounts/reports/fixed-asset-schedule" icon={<HddOutlined />}       label="Fixed Asset Sch."  collapsed={collapsed} />
            <NavRow to="/erp/accounts/reports/investment-schedule"  icon={<FundOutlined />}      label="Investment Sch."   collapsed={collapsed} />
            {/* Stage 6E.3 — Debtors / Creditors Aging. */}
            <NavRow to="/erp/accounts/reports/debtors-aging"        icon={<UserOutlined />}      label="Debtors Aging"     collapsed={collapsed} />
            <NavRow to="/erp/accounts/reports/creditors-aging"      icon={<ContainerOutlined />} label="Creditors Aging"   collapsed={collapsed} />
            <NavRow to="/erp/accounts/gst-reports"        icon={<FileTextOutlined />}    label="GST Reports"       collapsed={collapsed} />
          </nav>

          {/* Stage 6A foundation — sandbox link for the reusable ERP
              grid demo. Clearly marked so it's easy to remove later
              (delete this NavRow + its icon import + the route entry
              in routes/index.jsx). Visible to every authenticated
              user since the demo uses local state only — no master
              gate, no permission check. */}
          <nav
            className={`flex flex-col gap-px mt-1 ${collapsed ? '' : 'px-2'}`}
            aria-label="ERP sandbox navigation"
          >
            <NavRow
              to="/dev/erp-grid-demo"
              icon={<ExperimentOutlined />}
              label="Grid Demo"
              collapsed={collapsed}
            />
          </nav>

          {/* --- BILLING --- */}
          {isMaster ? (
            <>
              {!collapsed ? <SectionLabel>Billing</SectionLabel> : null}
              <nav
                className={`flex flex-col gap-px ${collapsed ? 'mt-2' : 'px-2'}`}
                aria-label="Billing navigation"
              >
                <NavRow
                  to="/subscription"
                  icon={<CreditCardOutlined />}
                  label="Subscription"
                  collapsed={collapsed}
                />
                <NavRow
                  to="/invoices"
                  icon={<FileTextOutlined />}
                  label="Invoices"
                  collapsed={collapsed}
                />
                {isSuspended ? (
                  <NavRow
                    icon={<StopOutlined />}
                    label="Users"
                    disabled
                    tooltip="Renew subscription to manage users"
                    collapsed={collapsed}
                  />
                ) : (
                  <NavRow
                    to="/users"
                    icon={<TeamOutlined />}
                    label="Users"
                    collapsed={collapsed}
                  />
                )}
              </nav>
            </>
          ) : null}
        </div>

        {/* Pinned: Log out — same row anatomy, rose-tinted hover so
            it reads as a destructive action without screaming. */}
        <div
          className={`shrink-0 border-t border-surface-200/70 ${
            collapsed ? 'py-2' : 'px-2 py-2'
          }`}
        >
          <NavRow
            icon={<LogoutOutlined />}
            label="Log out"
            action={() => logout()}
            danger
            collapsed={collapsed}
          />
        </div>

        {/* Pinned: status footer. Hidden when collapsed. The live
            dot + soft emerald halo + tracked label echo a status
            chip from Stripe / Linear's lower-chrome — quiet, but
            unmistakably "the system is up." */}
        {!collapsed ? (
          <div
            className="px-3.5 py-3 flex items-center gap-2 text-[11px] border-t border-surface-200/70 shrink-0"
            style={{
              background:
                'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
            }}
          >
            <span
              className="relative flex h-2 w-2 shrink-0"
              aria-hidden="true"
            >
              <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-50" />
              <span
                className="relative h-2 w-2 rounded-full bg-emerald-500"
                style={{ boxShadow: '0 0 0 2px rgba(16,185,129,0.18)' }}
              />
            </span>
            <span className="font-semibold text-ink-700 tracking-[0.02em]">
              Operational
            </span>
            <span
              aria-hidden="true"
              className="ml-auto inline-block h-1 w-1 rounded-full bg-surface-200"
            />
            <span className="font-medium text-ink-400 tracking-[0.08em] tabular-nums text-[10.5px]">
              v1.0
            </span>
          </div>
        ) : null}
      </div>
    </Sider>
  );
}
